import {
  UserDetails,
  getDecryptedValue,
  setEncryptedValue,
  deleteEncryptedValue,
} from 'common';
import Tracker from '@openreplay/tracker';
import trackerAssist, { Autostart } from '@openreplay/tracker-assist';
import { isAvMed } from 'context';
import i18n from '../../i18n';

export interface OpenReplayIdentifyPayload {
  userName?: string;
  id?: string;
  memberId?: string;
  memberIdHistory?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  dob?: string;
}

// Callback registry — components register handlers via setCoBrowseCallbacks
interface CoBrowseCallbacks {
  onConsentNeeded?: () => void;
  onAgentAllowed?: () => void;
  onAgentDisconnect?: () => void;
  onAgentDeclined?: () => void;
  onSessionEnded?: () => void;
}

let coBrowseCallbacks: CoBrowseCallbacks = {};

export const setCoBrowseCallbacks = (callbacks: CoBrowseCallbacks): void => {
  coBrowseCallbacks = callbacks;
};

export const clearCoBrowseCallbacks = (): void => {
  coBrowseCallbacks = {};
};

const formatDob = (value?: string | null): string => {
  const normalizedValue = toMetadataValue(value);
  if (!normalizedValue) return '';

  const isoLikeDateMatch = normalizedValue.match(
    /^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/
  );
  if (isoLikeDateMatch?.[1]) return isoLikeDateMatch[1];

  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return normalizedValue;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OPENREPLAY_PROJECT_KEY = sanitizeEnvValue(
  process.env.REACT_APP_OPENREPLAY_PROJECT_KEY
);
const OPENREPLAY_INGEST_POINT = sanitizeEnvValue(
  process.env.REACT_APP_OPENREPLAY_INGEST_POINT
);
const OPENREPLAY_DISABLE_SECURE_MODE = (() => {
  // Runtime env override allows disabling secure mode in production if needed
  const envOverride = process.env.REACT_APP_OPENREPLAY_DISABLE_SECURE_MODE;
  if (envOverride !== undefined) {
    return envOverride === 'false'; 
  }
  // Default: disable secure mode in dev, enable in prod
  return process.env.NODE_ENV === 'development';
})();

// OpenReplay Assist feature toggle. Cannot read React context from this plain
// module, so the value is pushed in from a component that has context access
// (see App.tsx -> setOpenReplayAssistEnabled). This gates session recording.
let openReplayAssistEnabled = false;

export const setOpenReplayAssistEnabled = (enabled: boolean): void => {
  openReplayAssistEnabled = !!enabled;
};

// Separate toggle that gates co-browsing (trackerAssist) only. Recording stays
// controlled by openReplayAssistEnabled above.
let openReplayCoBrowsingEnabled = false;

export const setOpenReplayCoBrowsingEnabled = (enabled: boolean): void => {
  openReplayCoBrowsingEnabled = !!enabled;
};

let trackerInstance: Tracker | null = null;
let recordingStarted = false;
let assistInstance: { stop: () => void; start: () => void } | null = null;
let isStarting = false; // lock to prevent concurrent starts
let suppressAgentDisconnectNotifications = false;

// waitStatus isn't on the public typed surface in every version — probe both spots.
const waitTrackerStopped = async (tracker: Tracker): Promise<void> => {
  const t = tracker as any;
  try {
    if (typeof t.waitStatus === 'function') await t.waitStatus(0);
    else if (typeof t.app?.waitStatus === 'function') await t.app.waitStatus(0);
  } catch (e) {
    console.warn(
      '[OpenReplay] waitStatus(0) unavailable or failed; continuing',
      e
    );
  }
};

// Hide vendor confirm overlay — we show our own CoBrowseConsentModal instead
const hideVendorConfirmOverlay = (): void => {
  if (document.getElementById('__or_hide_confirm')) return;
  const style = document.createElement('style');
  style.id = '__or_hide_confirm';
  style.textContent =
    '#openreplay-confirm-window-wrapper { display: none !important; }';
  document.head.appendChild(style);

  // Show our CoBrowseConsentModal whenever the vendor mounts its hidden modal
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const added = Array.from(mutation.addedNodes);
      if (added.some((n) => n instanceof HTMLElement && n.id === 'openreplay-confirm-window-wrapper')) {
        coBrowseCallbacks.onConsentNeeded?.();
        return;
      }
    }
  }).observe(document.body, { childList: true });
};

export const approveVendorConsent = (): void => {
  const btn = document.getElementById('openreplay-confirm-window-confirm-btn');
  if (btn) {
    (btn as HTMLButtonElement).click();
  } else {
    console.warn('[OpenReplay] Vendor confirm button not found in DOM');
  }
};

export const denyVendorConsent = (): void => {
  const btn = document.getElementById('openreplay-confirm-window-decline-btn');
  if (btn) {
    (btn as HTMLButtonElement).click();
  } else {
    console.warn('[OpenReplay] Vendor decline button not found in DOM');
  }
};


export const stopCoBrowsing = (): void => {
  if (!assistInstance) {
    console.warn(
      '[OpenReplay] No assist instance available to stop co-browsing'
    );
    return;
  }

  // User initiated stop: ignore trailing disconnect notifications coming
  // from the agent/app teardown path until a fresh agent connection occurs.
  suppressAgentDisconnectNotifications = true;
  assistInstance.stop();
  assistInstance.start();
  handleSuccessfulStart();
  coBrowseCallbacks.onAgentDisconnect?.();
};

function sanitizeEnvValue(value?: string): string {
  return (value || '').trim().replaceAll(/(^["'])|(["']$)/g, '');
}

function toMetadataValue(value?: string | null): string {
  return (value || '').trim();
}

function getCarrierMetadataValue(): string {
  return isAvMed() ? 'avmed' : 'sentara';
}

function applyCarrierMetadata(tracker: Tracker): void {
  tracker.setMetadata('carrier', getCarrierMetadataValue());
}

function ensureTracker(): Tracker | null {
  if (trackerInstance) {
    return trackerInstance;
  }

  if (!OPENREPLAY_PROJECT_KEY) {
    console.warn('[OpenReplay] Missing REACT_APP_OPENREPLAY_PROJECT_KEY');
    return null;
  }

  const tracker = new Tracker({
    projectKey: OPENREPLAY_PROJECT_KEY,
    ingestPoint: OPENREPLAY_INGEST_POINT || undefined,
    __DISABLE_SECURE_MODE: OPENREPLAY_DISABLE_SECURE_MODE,
    respectDoNotTrack: false,
    capturePerformance: true,
    captureExceptions: true,
    obscureInputDates: false,
    obscureInputNumbers: false,
    obscureInputEmails: false,
    obscureTextEmails: false,
    defaultInputMode: 0,
    inlineCss: 3,
    network: {
      disabled: true,
      capturePayload: false,
      failuresOnly: false,
      sessionTokenHeader: false,
      captureInIframes: true,
      ignoreHeaders: false,
    },
    connAttemptCount: 10,
    connAttemptGap: 2000,
  });

  trackerInstance = tracker;
  initializeCoBrowsing();
  return trackerInstance;
}

export async function initializeCoBrowsing(): Promise<void> {
  if (!openReplayCoBrowsingEnabled) {
    console.warn(
      '[OpenReplay] Co-browsing DISABLED — check OpenReplayCoBrowsing flag'
    );
    return;
  }

  if (assistInstance) {
    return;
  }

  const tracker = ensureTracker();
  if (!tracker) {
    console.warn('[OpenReplay] Cannot initialize Assist without tracker');
    return;
  }

  const socketHost = OPENREPLAY_INGEST_POINT
    ? OPENREPLAY_INGEST_POINT.replace('/ingest', '').replace(
        /^https?:\/\//,
        'wss://'
      )
    : undefined;
  assistInstance =
    tracker.use(
      trackerAssist({
        ...(socketHost ? { socketHost } : {}),
        callConfirm: {
          text: i18n.t('openReplay.confirm.callText'),
          confirmBtn: i18n.t('openReplay.confirm.acceptBtn'),
          declineBtn: i18n.t('openReplay.confirm.declineBtn'),
        },
        controlConfirm: {
          text: i18n.t('openReplay.confirm.controlText'),
          confirmBtn: i18n.t('openReplay.confirm.acceptBtn'),
          declineBtn: i18n.t('openReplay.confirm.declineBtn'),
        },
        recordingConfirm: {
          text: i18n.t('openReplay.confirm.recordingText'),
          confirmBtn: i18n.t('openReplay.confirm.acceptBtn'),
          declineBtn: i18n.t('openReplay.confirm.declineBtn'),
        },
        sessionConfirm: {
          text: i18n.t('openReplay.confirm.callText'),
          confirmBtn: i18n.t('openReplay.confirm.acceptBtn'),
          declineBtn: i18n.t('openReplay.confirm.declineBtn'),
        },
        requestConfirm: true,
        ignoreAnonymous: true,
        agentShortNames: true,
        autostart: Autostart.Continuation,
        onAgentConnect: () => {
          suppressAgentDisconnectNotifications = false;
          if (sessionStorage.getItem('__openreplay_session_confirm')) {
            coBrowseCallbacks.onAgentAllowed?.();
          }
          return () => {
            if (suppressAgentDisconnectNotifications) {
              return;
            }
            coBrowseCallbacks.onAgentDisconnect?.();
          };
        },
        onSessionConfirmApprove: () => {
          coBrowseCallbacks.onAgentAllowed?.();
        },
        onSessionConfirmDeny: () => {
          coBrowseCallbacks.onAgentDeclined?.();
        },
        onCallStart: () => {
          return () => {
          };
        },
        onRemoteControlStart: () => {
          return () => {
          };
        },
        onCallDeny: () => {
        },
        onRemoteControlDeny: ({ email, name }) => {
        },
        onRecordingDeny: ({ email, name }) => {
        },
      })
    ) || null;
  hideVendorConfirmOverlay();
}

const handleSuccessfulStart = async (): Promise<void> => {
  // Re-apply stored user data on session resume (e.g. after page refresh).
  // The tracker handles session continuity via its own localStorage —
  // do NOT pass sessionHash manually (per vendor guidance).
  const savedUserDataJson = await getDecryptedValue(UserDetails.sessionMeta);
  if (savedUserDataJson) {
    const savedUserData = JSON.parse(
      savedUserDataJson
    ) as OpenReplayIdentifyPayload;
    await identifyOpenReplayUser(savedUserData);
  }
};

export const startOpenReplayRecording = async (): Promise<void> => {
  if (!openReplayAssistEnabled) return;
  const tracker = ensureTracker();

  if (!tracker) {
    console.warn('[OpenReplay] No tracker instance available.');
    return;
  }

  // Guard: already started, currently starting, or tracker internally active
  if (recordingStarted) {
    return;
  }

  if (isStarting) {
    return;
  }

  if (tracker.isActive()) {
    recordingStarted = true;
    return;
  }

  applyCarrierMetadata(tracker);

  isStarting = true; // acquire lock

  try {
    const startedSession = await tracker.start();

    recordingStarted = !!startedSession?.success;

    if (startedSession?.success) {
      if (openReplayCoBrowsingEnabled && assistInstance) {
        assistInstance.start();
      }
      await handleSuccessfulStart();
    } else {
      console.warn('[OpenReplay] Session did not start successfully');
    }
  } catch (error) {
    console.error('[OpenReplay] Failed to start recording', error);
    recordingStarted = false; // reset so a retry is possible
  } finally {
    isStarting = false; // release lock always
  }
};

export const identifyOpenReplayUser = async (
  user: OpenReplayIdentifyPayload
): Promise<void> => {
  if (!openReplayAssistEnabled) return;
  if (!openReplayAssistEnabled) return;
  const tracker = ensureTracker();
  if (!tracker) {
    return;
  }
  
  const userId = toMetadataValue(user.id);
  const userName = toMetadataValue(user.userName);
  if (!userId && !userName) {
    console.warn('[OpenReplay] user.id or userName is required for identify');
    return;
  }
  if (userId) {
    tracker.setUserID(userId);
  }

  const firstName = toMetadataValue(user.firstName);
  const lastName = toMetadataValue(user.lastName);
  const email = toMetadataValue(user.email);
  const dob = formatDob(user.dob);
  const memberId = toMetadataValue(user.memberId);
  const memberIdHistory = toMetadataValue(user.memberIdHistory);

  applyCarrierMetadata(tracker);

  if (userName) {
    tracker.setMetadata('userName', userName);
  }

  if (memberId) {
    tracker.setMetadata('memberId', memberId);
  }

  if (memberIdHistory) {
    tracker.setMetadata('memberIdHistory', memberIdHistory);
  }

  if (firstName) {
    tracker.setMetadata('firstName', firstName.toLowerCase());
  }

  if (lastName) {
    tracker.setMetadata('lastName', lastName.toLowerCase());
  }

  if (email) {
    tracker.setMetadata('email', email);
  }

  if (dob) {
    tracker.setMetadata('dob', dob);
  }

  // Persist user data for session resume on refresh
  await setEncryptedValue(UserDetails.sessionMeta, JSON.stringify(user));
};

export const updatedMemberid = async (
  memberId: string,
  memberHistory: string
): Promise<void> => {
  if (!openReplayAssistEnabled) return;
  const tracker = ensureTracker();
  if (!tracker) {
    console.warn('[OpenReplay] No tracker instance available.');
    return;
  }
  tracker.setMetadata('memberId', memberId);
  tracker.setMetadata('memberIdHistory', memberHistory); // for legacy support
};

// Applies the current member id to OpenReplay and maintains a de-duplicated
// history of every member id the user has used (e.g. across multi-plan switches).
// Switching back to a previously used plan does not add a duplicate.
export const tagOpenReplayMemberId = async (
  memberId: string
): Promise<void> => {
  if (!openReplayAssistEnabled) return;
  const id = toMetadataValue(memberId);
  if (!id) return;

  const stored = await getDecryptedValue(UserDetails.memberIdHistory);
  const history: string[] = stored ? JSON.parse(stored) : [];
  if (!history.includes(id)) {
    history.push(id);
    await setEncryptedValue(
      UserDetails.memberIdHistory,
      JSON.stringify(history)
    );
  }

  await updatedMemberid(id, history.join(','));
};

export interface OpenReplayRegistrationTags {
  firstName?: string;
  lastName?: string;
  dob?: string;
}
export const setOpenReplayRegistrationTags = async (
  tags: OpenReplayRegistrationTags
): Promise<void> => {
  if (!openReplayAssistEnabled) return;
  const tracker = ensureTracker();
  if (!tracker) {
    return;
  }

  applyCarrierMetadata(tracker);

  const firstName = toMetadataValue(tags.firstName);
  const lastName = toMetadataValue(tags.lastName);
  const dob = formatDob(tags.dob);

  if (firstName) {
    tracker.setMetadata('firstName', firstName);
  }

  if (lastName) {
    tracker.setMetadata('lastName', lastName);
  }

  if (dob) {
    tracker.setMetadata('dob', dob);
  }
};

export const stopOpenReplaySession = async (
  { restart = true }: { restart?: boolean } = {}
): Promise<void> => {
  if (!openReplayAssistEnabled) return;
  const tracker = ensureTracker();
  if (!tracker) {
    console.warn('[OpenReplay] No tracker instance available.');
    return;
  }
  assistInstance?.stop();
  tracker.stop();

  recordingStarted = false;
  isStarting = true; // guard against concurrent starts while tearing down
  deleteEncryptedValue();
  coBrowseCallbacks.onSessionEnded?.();

  await waitTrackerStopped(tracker);

  try {
    // Pre-login idle timeout passes restart=false: recording stays stopped until
    // the next user activity or login starts a fresh session. Sign-out/logout
    // restarts a fresh anonymous session immediately.
    if (!restart) return;

    const started = await tracker.start({ forceNew: true });
    if (started?.success) {
      recordingStarted = true;
      // Do NOT restart assist here: it would re-set the vendor continuation flag
      // and reconnect the agent, re-triggering the consent modal after sign-out.
    } else {
      console.warn(
        '[OpenReplay] forceNew start did not succeed; next load will retry',
        started
      );
    }
  } finally {
    isStarting = false; // always release the lock so future starts aren't blocked
  }
};
